import type { ComponentType } from 'react';
import type { AddWizardValues } from '../../schemas';
import { StepType } from './StepType';
import { StepRole } from './StepRole';
import { StepIdentity } from './StepIdentity';
import { StepBrief } from './StepBrief';
import { StepConfirm } from './StepConfirm';

export type WizardStepId = 'type' | 'role' | 'identity' | 'brief' | 'confirm';
export type TerminalId = 'success' | 'human-coming-soon' | 'add-human';

export interface AdvanceCtx {
  /** Loads Staff Profile detail and prefills first_name, last_name, personality,
   * systemPrompt into the form. Caches the detail for "Reset to defaults". */
  prefillFromStaffProfile: (staffProfileId: string) => Promise<void>;
}

export interface StepDef {
  id: WizardStepId;
  label: string;
  Component: ComponentType;
  canContinue: (values: AddWizardValues) => boolean;
  onAdvance?: (values: AddWizardValues, ctx: AdvanceCtx) => Promise<void>;
  nextId?: (values: AddWizardValues) => WizardStepId | TerminalId;
  continueLabel?: (values: AddWizardValues) => string;
}

export const WIZARD_STEPS: StepDef[] = [
  {
    id: 'type',
    label: 'Type',
    Component: StepType,
    canContinue: (v) => Boolean(v.memberKind),
    nextId: (v) => (v.memberKind === 'human' ? 'human-coming-soon' : 'role'),
  },
  {
    id: 'role',
    label: 'Describe the position',
    Component: StepRole,
    canContinue: (v) => v.staffProfileId.length > 0 || v.roleSkipped,
    onAdvance: async (v, ctx) => {
      // Custom-from-scratch path: nothing to prefill. The user fills
      // first_name / personality / systemPrompt directly in the next steps.
      if (v.roleSkipped) return;
      await ctx.prefillFromStaffProfile(v.staffProfileId);
    },
  },
  {
    id: 'identity',
    label: 'Identity',
    Component: StepIdentity,
    canContinue: (v) => v.first_name.trim().length > 0,
  },
  {
    id: 'brief',
    label: 'Brief',
    Component: StepBrief,
    canContinue: (v) => v.personality.trim().length > 0 && v.systemPrompt.trim().length > 0,
  },
  {
    id: 'confirm',
    label: 'Confirm',
    Component: StepConfirm,
    canContinue: () => true,
    continueLabel: (v) => `Add ${v.first_name}`,
  },
];

/** Map from terminal screens back to the step that owns "Back". null = true terminus. */
export const TERMINAL_BACK: Record<TerminalId, WizardStepId | null> = {
  'human-coming-soon': 'type',
  'add-human': null,
  success: null,
};

/** Set of all terminal ids — used by the controller to detect when an
 *  onAdvance/nextId target should switch to a terminal screen rather than
 *  another wizard step. Add new terminals here and in TERMINAL_BACK above
 *  rather than touching the controller. */
export const TERMINAL_IDS = new Set<TerminalId>(['success', 'human-coming-soon', 'add-human']);
export function isTerminalId(id: WizardStepId | TerminalId): id is TerminalId {
  return TERMINAL_IDS.has(id as TerminalId);
}
